import { Suspense, useEffect, useRef } from "react";
import { MUTATIONS, useMutation } from "../../../../../api";
import { Spinner } from "../../../../../design-system/utils/Spinner/Spinner.jsx";
import Layout from "../../../../../design-system/components/Layout/Layout.jsx";
import AvatarGroup from "../../../../../design-system/components/AvatarGroup/AvatarGroup.jsx";
import { TeamMemberNames } from "../TeamMemberNames/TeamMemberNames.jsx";
import OtherFormFields from "../OtherFormFields/OtherFormFields.jsx";
import DescriptionWithNewLines from "../DescriptionWithNewLines/DescriptionWithNewLines.jsx";
import { ReactionBar } from "../ReactionBar/ReactionBar.jsx";
import Comments from "../Comments/Comments.jsx";
import { CloseButton } from "../../../../../design-system/components/CloseButton/CloseButton.jsx";
import { UploadContent } from "../UploadContent/UploadContent.jsx";
import styles from "./FullShowroomEntryWithData.css.ts";

const Tags = ({ tags }) => (
  <>
    {tags && tags.length > 0 && (
      <ul className={styles.tags}>
        {tags.map((tag) => (
          <li key={tag} className={styles.fieldValue}>
            #{tag}
          </li>
        ))}
      </ul>
    )}
  </>
);

const useVisit = ({ entry, sparkId }) => {
  const [doVisit] = useMutation(MUTATIONS.addVisit);
  const latestEntryRef = useRef({ ...entry, sparkId });
  useEffect(() => {
    latestEntryRef.current = { ...entry, sparkId };
  }, [entry, sparkId]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      doVisit({
        workshopId: latestEntryRef.current.workshopId,
        entryId: latestEntryRef.current.id,
        smallTeamId: latestEntryRef.current.smallTeamId,
        sparkId: latestEntryRef.current.sparkId,
      });
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [doVisit]);
};

export const FullShowroomEntryWithData = (props) => {
  const {
    entry,
    teamMembers,
    comments,
    myReaction,
    myCommentReactions,
    me,
    myRoles,
    commentsEnabled,
    unseenComments,
    isPublic,
    sparkId,
    workshopId,
    backPath,
  } = props;

  const { title, upload, coverImage, tags, description } = entry.formData;
  useVisit({ entry, sparkId });

  return (
    <Suspense fallback={<Spinner />}>
      <Layout>
        <div className={styles.onlyButton}>
          <CloseButton back={backPath} />
        </div>
        <div className={styles.rootContainer}>
          <article className={styles.innerContainer}>
            {(coverImage || upload) && (
              <UploadContent data={upload} coverFileData={coverImage} />
            )}
            <div className={styles.contentWrapper}>
              <header className={styles.avatarTagsRow}>
                {teamMembers && !!teamMembers.length && (
                  <AvatarGroup users={teamMembers} />
                )}
                <Tags tags={tags} />
              </header>
              <section className={styles.sectionSpacing}>
                {title && <h1 className={styles.title}>{title}</h1>}

                {teamMembers && !!teamMembers.length && (
                  <section
                    className={styles.mainInfoCol}
                    aria-labelledby="team-label"
                  >
                    <h2 id="team-label" className={styles.label}>
                      Projekt von
                    </h2>
                    <TeamMemberNames users={teamMembers} color="gray400" />
                  </section>
                )}

                <OtherFormFields formData={entry.formData} styles={styles} />

                <section aria-label="Beschreibung">
                  <DescriptionWithNewLines
                    containerStyle={styles.fieldValue}
                    paragraphStyle={styles.fieldValueParagraph}
                    description={description}
                  />
                </section>
              </section>

              <section
                className={styles.sectionSpacing}
                aria-label="Reaktionen"
              >
                <ReactionBar
                  entry={entry}
                  me={me}
                  myReaction={myReaction}
                  sparkId={sparkId}
                />
              </section>

              {commentsEnabled && (
                <section aria-label="Kommentare">
                  <Comments
                    comments={comments}
                    me={me}
                    myRoles={myRoles}
                    workshopId={workshopId}
                    entryId={entry.id}
                    smallTeamId={entry.smallTeamId}
                    myCommentReactions={myCommentReactions}
                    unseenComments={unseenComments}
                    isPublic={isPublic}
                    sparkId={sparkId}
                  />
                </section>
              )}
            </div>
          </article>
        </div>
      </Layout>
    </Suspense>
  );
};
